// A fight's ground off the board, in the commissioned encounter art (docs/186): the set a fight in that kit,
// biome, season and hour would be drawn in (`chooseSet`, as `TileRenderer` asks for it), loaded on demand.
// `useEncounterSet` gives the set once it has loaded (undefined until then, or when the kit has no art or
// `?oryx=1`), so a screen keeps its Oryx look meanwhile and swaps when the art lands. `EncounterGround` is
// `PixelTerrain`'s stand-in for a fight board: the painted board flattened (`compositeBoard`) onto a canvas.
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { BoardLike } from '../../game/tiling';
import { kitFor } from '../../game/tiling';
import { loadSet } from '../../game/encounter/atlas';
import { boardNeedsCountry, chooseSet, countrySetFor, setIdsAvailable } from '../../game/encounter/sets';
import { borrowsFromCountry } from '../../game/encounter/paint/context';
import { compositeBoard, compositeCanvas } from '../../game/encounter/composite';
import { paintBoard } from '../../game/encounter/paint';
import type { EncounterSet } from '../../game/encounter/types';
import { parseSeason, seasonHour } from '../../game/weather/season';
import { PixelTerrain } from './PixelTerrain';

/** `?oryx=1`: keep the Oryx look (as `EncounterTerrain.oryxForced`, without pulling the Phaser renderer in). */
function oryxForced(): boolean {
  try { return new URLSearchParams(window.location.search).get('oryx') === '1'; } catch { return false; }
}

/** What a fight's ground is drawn in: its kit, the World's biome and season (any case), the hour on the board, and an outdoor place's country (EA14). */
export interface GroundWant { kit: string; biome?: string; season?: string; hour?: number; country?: string }

/** The set id for `want` (the kit's own name first, then the Oryx kit it is drawn as), or undefined: keep the Oryx look. */
export function encounterSetFor(want: GroundWant): string | undefined {
  if (oryxForced()) return undefined;
  const season = parseSeason(want.season);
  // night by the hour the light is drawn at (a summer evening is still light), as the board judges it
  const hour = want.hour !== undefined && season ? seasonHour(want.hour, season) : want.hour;
  const ask = { biome: want.biome || undefined, season: season ?? undefined, hour };
  const avail = setIdsAvailable();
  const oryx = kitFor(want.kit).key;
  return (chooseSet({ kit: want.kit, ...ask }, avail) ?? (oryx !== want.kit ? chooseSet({ kit: oryx, ...ask }, avail) : undefined))?.id;
}

/** The loaded set for `want` (undefined while it loads, if it fails, or when there is none). */
export function useEncounterSet(want: GroundWant | undefined): EncounterSet | undefined {
  // cheap: the choice reads a cached index of the build's sets
  const id = want ? encounterSetFor(want) : undefined;
  const [set, setSet] = useState<EncounterSet>();
  useEffect(() => {
    if (!id) { setSet(undefined); return; }
    let live = true;
    loadSet(id).then((s) => { if (live) setSet(s); }, (e) => { if (import.meta.env.DEV) console.warn(`encounter art: ${String(e)}`); });
    return () => { live = false; };
  }, [id]);
  return set && set.id === id ? set : undefined;
}

/**
 * The country set a place's `set` borrows its water, rough and thicket from (docs/186 EA14), once loaded; undefined
 * while it loads, or when the place has its own or `board` has no square that needs it.
 */
export function useCountrySet(set: EncounterSet | undefined, country: string | undefined, board: BoardLike | undefined): EncounterSet | undefined {
  const id = set && board && borrowsFromCountry(set) && boardNeedsCountry(board) ? countrySetFor(set.id, country) : undefined;
  const [got, setGot] = useState<EncounterSet>();
  useEffect(() => {
    if (!id) { setGot(undefined); return; }
    let live = true;
    loadSet(id).then((s) => { if (live) setGot(s); }, (e) => { if (import.meta.env.DEV) console.warn(`encounter art: ${String(e)}`); });
    return () => { live = false; };
  }, [id]);
  return got && got.id === id ? got : undefined;
}

export interface EncounterGroundProps extends GroundWant {
  board: BoardLike;
  /** Whole-number CSS scale of the 24 px cells. */
  scale?: number;
  /** Cycle the Oryx look's animated frames while it shows. */
  animate?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** A board drawn as a fight would draw it: the Oryx look until its set loads, then the painted board. */
export function EncounterGround({ board, kit, biome, season, hour, country, scale = 2, animate, className = '', style }: EncounterGroundProps) {
  const set = useEncounterSet({ kit, biome, season, hour });
  const land = useCountrySet(set, country, board);
  const art = useMemo(() => {
    if (!set) return undefined;
    // the rows above the board are the screen's own: what rises over row 0 is cut
    const c = compositeBoard(paintBoard(board, set, land ? { country: land } : {}), set, { top: 0 });
    return compositeCanvas(c);
  }, [board, set, land]);
  const [el, setEl] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const ctx = el?.getContext('2d');
    if (!ctx || !art) return;
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.drawImage(art, 0, 0);
  }, [el, art]);
  if (!art || !set) return <PixelTerrain board={board} kit={kit} scale={scale} animate={animate} className={className} style={style} />;
  return (
    <canvas ref={setEl} className={`fk-terrain ${className}`} width={board.w * 24} height={board.h * 24} aria-hidden="true" data-ready="true" data-art={set.id}
      style={{ width: board.w * 24 * scale, height: board.h * 24 * scale, imageRendering: 'pixelated', ...style }} />
  );
}
